import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, Dialog, DialogContent, Grid } from '@mui/material'
import PropTypes from 'prop-types'
import { isNil } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import Input from 'common/components/form/Input'
import Autocomplete from 'common/components/form/Autocomplete'
import { aquaFetch } from 'common/utils/ActionUtils'
import ApplicationConf from 'conf/ApplicationConf'
import { fetchUsers, selectUsers } from 'admin/users/UsersSlice'
import ContributorIcon from './ContributorIcon'

// Dialog de création / édition d'un CONTACT partagé (table parc_contacts), indépendant d'un client.
// Interne Aquasys (login) : nom/email repris de users, non modifiables. Externe : tout modifiable.
const ContactFormDialog = ({
    contact: contactDefault,
    onClose = () => {},
    onSaved = () => {},
}) => {
    const dispatch = useDispatch()
    const [contact, setContact] = useState(contactDefault ?? {})
    const [saving, setSaving] = useState(false)
    const users = useSelector(selectUsers, shallowEqual)

    const isEdit = !isNil(contactDefault?.idContact)

    useEffect(() => {
        if (!users.length) {
            dispatch(fetchUsers())
        }
    }, [dispatch, users])

    const userList = useMemo(
        () => users.filter(u => u.email.includes('aquasys')).map(u => ({
            id: u.login,
            label: u.email.split('@')[0].replace('.', ' '),
            name: u.email.split('@')[0].replace('.', ' '),
        })),
        [users],
    )

    const change = changes => setContact(c => ({ ...c, ...changes }))

    const validate = () => {
        setSaving(true)
        const payload = {
            idContact: contact.idContact,
            login: contact.login,
            name: contact.name,
            description: contact.description,
            telephone: contact.telephone,
            email: contact.email,
        }
        aquaFetch(ApplicationConf.gdl.contacts(), { method: isEdit ? 'PUT' : 'POST', body: payload })
            .then(res => {
                const idContact = isEdit ? contact.idContact : res?.id
                onSaved({ ...contact, idContact })
                onClose()
            })
            .catch(() => toast.error('Erreur lors de l\'enregistrement du contact'))
            .finally(() => setSaving(false))
    }

    return (
        <Dialog open maxWidth='lg'>
            <BasicDialogTitle onClose={onClose}>
                {isEdit ? 'Modifier le contact' : 'Créer un contact'}
            </BasicDialogTitle>
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Card>
                    <CardContent>
                        <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' style={{ paddingTop: '5px' }}>
                            <Grid size={2}>
                                <ContributorIcon contributor={contact} />
                            </Grid>
                            <Grid size={10} container columnSpacing={2} rowSpacing={1} alignItems='center'>
                                <Grid size={6}>
                                    <Autocomplete
                                        label='Nom (si intervenant Aquasys)'
                                        items={userList}
                                        value={contact.login}
                                        onChange={login => change({
                                            login,
                                            name: userList.find(u => u.id === login)?.name,
                                            email: users.find(u => u.login === login)?.email,
                                        })}
                                        disabled={!contact.login && !!contact.name}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Input
                                        label='Nom (si intervenant Externe)'
                                        value={contact.name}
                                        onChange={name => change({ name })}
                                        disabled={!!contact.login}
                                        required
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Input
                                        label='Email'
                                        value={contact.email}
                                        onChange={email => change({ email })}
                                        disabled={!!contact.login}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <Input
                                        label='Téléphone'
                                        value={contact.telephone}
                                        onChange={telephone => change({ telephone })}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <Input
                                        label='Description'
                                        value={contact.description}
                                        onChange={description => change({ description })}
                                        multiline
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <BasicDialogAction
                buttonLabel={isEdit ? 'Modifier' : 'Créer'}
                onValidate={validate}
                isDisabled={!contact.name || saving}
                isLoading={saving}
            />
        </Dialog>
    )
}

ContactFormDialog.propTypes = {
    contact: PropTypes.shape({
        idContact: PropTypes.number,
        login: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        telephone: PropTypes.string,
        email: PropTypes.string,
    }),
    onClose: PropTypes.func,
    onSaved: PropTypes.func,
}

export default ContactFormDialog
